export const PRODUCT_ANNOUNCEMENT_KINDS = [
  { value: "featureRelease", label: "Feature release", tag: "New feature" },
  { value: "serviceNotice", label: "Service notice", tag: "Service notice" },
  {
    value: "appUpdate",
    label: "App update release notes (does not force an update)",
    tag: "App update",
  },
];

export const PRODUCT_ANNOUNCEMENT_TARGETS = [
  {
    value: "mobileUser",
    label: "App · Agents",
    description: "Signed-in agent accounts using the mobile app.",
  },
  {
    value: "mobileVendor",
    label: "App · Vendors",
    description: "Signed-in vendor accounts using the mobile app.",
  },
  {
    value: "mobileBuyer",
    label: "App · Buyers",
    description: "Signed-in buyer accounts using the mobile app.",
  },
  {
    value: "webUser",
    label: "Web · Agents (not live yet)",
    description: "Preview only until the Agent Web recipient is implemented.",
    disabled: true,
  },
];

export const PRODUCT_ANNOUNCEMENT_AUDIENCE_SCOPES = [
  {
    value: "all",
    label: "Everyone in selected roles",
    description: "All signed-in accounts matching the selected App/Web roles.",
  },
  {
    value: "organizations",
    label: "Selected organizations",
    description: "Matching accounts linked to one or more organizations.",
  },
  {
    value: "accounts",
    label: "Specific people",
    description: "Only the individual accounts selected below.",
  },
];

export const PRODUCT_ANNOUNCEMENT_ACCOUNT_TYPES = [
  { value: "User", label: "Agents", targetValues: ["mobileUser", "webUser"] },
  { value: "VendorAccount", label: "Vendors", targetValues: ["mobileVendor"] },
  { value: "BuyerAccount", label: "Buyers", targetValues: ["mobileBuyer"] },
];

const audienceRoleNames = {
  mobileUser: "agents",
  mobileVendor: "vendors",
  mobileBuyer: "buyers",
  webUser: "web agents",
};

const joinWithAnd = (values) =>
  values.length > 1
    ? `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`
    : values[0] || "";

export const describeAnnouncementAudience = (audience, organizationNames) => {
  const roles = joinWithAnd(
    audience.targets.map((target) => audienceRoleNames[target]),
  );
  if (!roles) return "Nobody yet — choose at least one account type.";
  if (audience.scope === "organizations") {
    return organizationNames.length
      ? `All app ${roles} in ${joinWithAnd(organizationNames)}.`
      : `App ${roles} — pick at least one organization.`;
  }
  if (audience.scope === "accounts") {
    const count = audience.accounts.length;
    return count
      ? `Only the ${count} ${count === 1 ? "person" : "people"} selected below.`
      : "Nobody yet — select at least one person below.";
  }
  return `All app ${roles}, across every organization.`;
};

export const PRODUCT_ANNOUNCEMENT_STATUS = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-800" },
  scheduled: { label: "Scheduled", className: "bg-blue-100 text-blue-800" },
  live: { label: "Live", className: "bg-green-100 text-green-800" },
  ended: { label: "Ended", className: "bg-amber-100 text-amber-900" },
  archived: { label: "Archived", className: "bg-slate-200 text-slate-700" },
};

let localActionSequence = 0;
let localMediaSequence = 0;

export const createStagedMedia = (file, altText, caption) => {
  localMediaSequence += 1;
  return {
    clientId: `media-${Date.now()}-${localMediaSequence}`,
    type: file.type.startsWith("video/") ? "video" : "image",
    url: URL.createObjectURL(file),
    altText: altText.trim(),
    caption: caption.trim(),
    file,
  };
};

export const releaseStagedMedia = (media) =>
  media.forEach((asset) => asset.file && URL.revokeObjectURL(asset.url));

export const createAnnouncementAction = () => {
  localActionSequence += 1;
  return {
    clientId: `action-${Date.now()}-${localActionSequence}`,
    label: "",
    appPath: "",
    webPath: "",
    externalUrl: "",
    style: "primary",
  };
};

export const EMPTY_PRODUCT_ANNOUNCEMENT = {
  name: "",
  kind: "featureRelease",
  title: "",
  body: "",
  highlights: [],
  media: [],
  actions: [],
  audience: {
    targets: [],
    scope: "all",
    organizationIds: [],
    accounts: [],
  },
  audienceAccountDetails: [],
  startsAt: "",
  endsAt: "",
  priority: 50,
};

export const toLocalDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export const toAnnouncementForm = (announcement) => ({
  ...EMPTY_PRODUCT_ANNOUNCEMENT,
  ...announcement,
  highlights: announcement?.highlights || [],
  media: announcement?.media || [],
  actions: (announcement?.actions || []).map((action) => ({
    ...createAnnouncementAction(),
    ...action,
  })),
  audience: {
    targets: announcement?.audience?.targets || [],
    scope: announcement?.audience?.scope || "all",
    organizationIds: (announcement?.audience?.organizationIds || []).map(
      (organization) => String(organization?._id || organization),
    ),
    accounts: (announcement?.audience?.accounts || []).map((account) => ({
      accountModel: account.accountModel,
      accountId: String(account.accountId?._id || account.accountId),
    })),
  },
  audienceAccountDetails: announcement?.audienceAccountDetails || [],
  startsAt: toLocalDateTimeInput(announcement?.startsAt),
  endsAt: toLocalDateTimeInput(announcement?.endsAt),
});

const toIsoDate = (value) => (value ? new Date(value).toISOString() : null);

export const toAnnouncementPayload = (form) => ({
  name: form.name,
  kind: form.kind,
  title: form.title,
  body: form.body,
  highlights: form.highlights.filter((highlight) => highlight.trim()),
  media: form.media
    .filter((asset) => asset._id)
    .map((asset) => ({
      _id: asset._id,
      altText: asset.altText || "",
      caption: asset.caption || "",
    })),
  actions: form.actions.map((action) => ({
    ...(action._id && { _id: action._id }),
    label: action.label || "",
    appPath: action.appPath || "",
    webPath: action.webPath || "",
    externalUrl: action.externalUrl || "",
    style: action.style || "primary",
  })),
  audience: {
    targets: form.audience.targets,
    scope: form.audience.scope,
    organizationIds:
      form.audience.scope === "organizations"
        ? form.audience.organizationIds
        : [],
    accounts:
      form.audience.scope === "accounts" ? form.audience.accounts : [],
  },
  startsAt: toIsoDate(form.startsAt),
  endsAt: toIsoDate(form.endsAt),
  priority: Number(form.priority),
});

export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const formatAnnouncementDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-AU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "No limit";
